// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/** Instrument definitions and the string-matching helper. Pure data + maths. */

import type { InstrumentDef, InstrumentId, StringDef } from './types';
import { centsBetween, midiToFrequency, noteLabel } from './notes';

function strings(midis: number[]): StringDef[] {
  return midis.map((midi) => ({ midi, label: noteLabel(midi) }));
}

// Standard tunings, low string first (A4 = MIDI 69).
export const INSTRUMENTS: Record<InstrumentId, InstrumentDef> = {
  chromatic: { id: 'chromatic', name: 'Chromatic', strings: [] },
  guitar: {
    id: 'guitar',
    name: 'Guitar',
    // E2 A2 D3 G3 B3 E4
    strings: strings([40, 45, 50, 55, 59, 64]),
  },
  bass: {
    id: 'bass',
    name: 'Bass',
    // E1 A1 D2 G2
    strings: strings([28, 33, 38, 43]),
  },
  ukulele: {
    id: 'ukulele',
    name: 'Ukulele',
    // g4 C4 E4 A4 (reentrant high-G standard)
    strings: strings([67, 60, 64, 69]),
  },
  violin: {
    id: 'violin',
    name: 'Violin',
    // G3 D4 A4 E5
    strings: strings([55, 62, 69, 76]),
  },
};

export const INSTRUMENT_ORDER: InstrumentId[] = ['chromatic', 'guitar', 'bass', 'ukulele', 'violin'];

export function getInstrument(id: InstrumentId): InstrumentDef {
  return INSTRUMENTS[id] ?? INSTRUMENTS.chromatic;
}

/**
 * The open string closest to a detected frequency, by absolute cents distance.
 * Returns -1 for an empty instrument or a non-positive frequency.
 */
export function nearestStringIndex(hz: number, inst: InstrumentDef, referenceA = 440): number {
  if (!(hz > 0) || inst.strings.length === 0) return -1;
  let best = -1;
  let bestCents = Infinity;
  for (let i = 0; i < inst.strings.length; i++) {
    const target = midiToFrequency(inst.strings[i].midi, referenceA);
    const dist = Math.abs(centsBetween(hz, target));
    if (dist < bestCents) {
      bestCents = dist;
      best = i;
    }
  }
  return best;
}
